import { useBizProductContext } from '../BizProductProvider.js'
import type { ProductVariant, FreeGift, Bundle } from '../types/product'
import { checkItemAvailableForProduct } from '../utils/helper.js'
import { useDiscountEnable } from './useDiscountEnable.js'

import { useAiuiContext } from '../../AiuiProvider/index.js'

interface BenefitsParams {
  variant: ProductVariant
}

interface BenefitItemConfig<T extends Record<string, any> = Record<string, any>> {
  enable: boolean
  config?: T // 原始配置
}

interface BenefitsResult {
  /**
   * 通用优惠券
   */
  commonCoupon?: BenefitItemConfig
  /**
   * 买赠
   */
  freeGift: BenefitItemConfig<FreeGift>
  /**
   * 捆绑折扣
   */
  bundle: BenefitItemConfig<Bundle>
  /**
   * 会员价
   */
  memberDiscount: BenefitItemConfig
  /**
   * 积分抵现
   */
  creditsRedemption: BenefitItemConfig
  /**
   * 高标物流
   */
  paidShipping: BenefitItemConfig

  /**
   * 满赠
   */
  fullGift: BenefitItemConfig

  /**
   * 满减折扣
   */
  levelDiscount: BenefitItemConfig
}

/**
 * 商品权益
 * 获取当前sku 所有的权益
 * 包括 通用优惠券，会员价，满减，捆绑折扣，买赠 ，满赠 ，积分抵现
 * @returns
 */
export const useBenefits = ({ variant }: BenefitsParams): BenefitsResult => {
  const { coupon, product, bundle, freeGift, memberFunctionResult, creditsRedemption, profile } = useBizProductContext()

  const { amount: creditsRedemptionAmount } = creditsRedemption ?? {}

  const { trackingData } = useAiuiContext()

  const total = Math.max(
    memberFunctionResult?.withCouponMemberTotalSave ?? 0,
    memberFunctionResult?.withoutCouponMaxMemberTotalSave ?? 0
  )

  const discountEnable = useDiscountEnable({ profile, variant, memberFunctionResult, coupon })

  // 高标物流
  const showPaidShipping = Boolean(
    trackingData?.paidShipping &&
    variant.requiresShipping &&
    !variant?.metafields?.global?.presell &&
    !variant?.metafields?.global?.HideShipping
  )

  // 脚本满赠
  const fullGift = trackingData?.freeGift
  const excludeProducts =
    fullGift?.excludeProducts?.map((item: any) => (typeof item === 'object' && 'sku' in item ? item.sku : item)) ?? []

  const showLevelDiscount =
    trackingData?.levelDiscount && checkItemAvailableForProduct({ product, variant, item: trackingData?.levelDiscount })

  return {
    commonCoupon: {
      enable: discountEnable.showCommonCoupon && Boolean(coupon),
      config: coupon ?? {},
    },
    freeGift: {
      enable: Boolean(freeGift) && !!freeGift?.giftList?.length,
      config: freeGift,
    },
    bundle: {
      enable:
        Boolean(bundle) &&
        !!bundle?.bundleList?.length &&
        Math.max(...(bundle?.bundleList?.map(item => item.savings ?? 0) ?? [])) > 0, // 有优惠才展示
      config: bundle,
    },
    memberDiscount: {
      enable: discountEnable.showMemberDiscount,
      // enable: total > 0,
      config: {
        amount: total,
        description: memberFunctionResult?.labels?.[0],
      },
    },
    creditsRedemption: {
      enable: Boolean(creditsRedemptionAmount) && creditsRedemptionAmount! > 0,
      config: { amount: creditsRedemptionAmount, label: trackingData?.creditsRedemptionLabel },
    },
    paidShipping: {
      enable: showPaidShipping,
      config: {},
    },
    fullGift: {
      enable: Boolean(fullGift && !excludeProducts.includes(variant.sku)),
      config: fullGift ?? {},
    },
    levelDiscount: {
      enable: Boolean(showLevelDiscount),
      config: trackingData?.levelDiscount ?? {},
    },
  }
}
